import React, { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import OrderDetails from '../orders/OrderDetails';
import SupplierSessionPanel from './SupplierSessionPanel';
import { ORDER_STATUS, CURRENCY_CODE } from '../../constants';
import { isLocalCurrency, sanitizePhoneInput } from '../../utils';
import { useExchangeRates } from '../../hooks/useExchangeRates';
import { formatLocalizedDate, formatLocalizedLongDate } from '../../i18n/localeUtils';
import { useTranslation, Trans } from 'react-i18next';
import { isTulKanatProductType, restoreTulKanatStockForOrder, isQuantityStockProductType, restoreQuantityStockForOrder } from '../../utils/stockUtils';
import { recalculateCommissionForUser, reverseCommissionFromUser } from '../../utils/commissionUtils';
import { generateDealerCombinedCustomerPDF, generateDealerCombinedLabelPDF } from '../../utils/pdfGenerators';
import './DealerList.css';

const parseSessionDateValue = (session) => {
  const raw = session?.orderDate || session?.sessionDate;
  if (!raw) return null;
  const normalized = String(raw).includes('T') ? raw : `${raw}T00:00:00`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const filterSessionsByDateRange = (sessionList, from, to) => {
  if (!from && !to) return sessionList;
  return sessionList.filter((session) => {
    const date = parseSessionDateValue(session);
    if (!date) return false;
    if (from && date < new Date(`${from}T00:00:00`)) return false;
    if (to && date > new Date(`${to}T23:59:59`)) return false;
    return true;
  });
};

const fetchOrdersGroupedBySessions = async (sessionIds) => {
  const grouped = {};
  sessionIds.forEach((id) => {
    grouped[id] = [];
  });

  for (let i = 0; i < sessionIds.length; i += 10) {
    const batch = sessionIds.slice(i, i + 10);
    const ordersQuery = batch.length === 1
      ? query(collection(db, 'orders'), where('customerId', '==', batch[0]))
      : query(collection(db, 'orders'), where('customerId', 'in', batch));
    const snapshot = await getDocs(ordersQuery);
    snapshot.docs.forEach((orderDoc) => {
      const data = orderDoc.data();
      const sessionId = data.customerId;
      if (grouped[sessionId]) {
        grouped[sessionId].push({ id: orderDoc.id, ...data });
      }
    });
  }

  return grouped;
};

// Bayi'ye özgü sipariş/kargo durumları (perakende workflow'undan bağımsız)
const DEALER_STATUS = {
  NEW_ORDER: 'new_order',
  PREPARING: 'preparing',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled'
};

const DEALER_STATUS_CLASS = {
  [DEALER_STATUS.NEW_ORDER]: 'status-new',
  [DEALER_STATUS.PREPARING]: 'status-production',
  [DEALER_STATUS.SHIPPED]: 'status-ready',
  [DEALER_STATUS.DELIVERED]: 'status-completed',
  [DEALER_STATUS.CANCELLED]: 'status-cancelled'
};

const DEALER_STATUS_KEYS = Object.values(DEALER_STATUS);

const FIRM_TYPE_OPTIONS = ['bayi', 'tedarikci', 'bayi_tedarikci'];

const FIRM_TYPE_ICONS = {
  bayi: 'fa-store',
  tedarikci: 'fa-truck',
  bayi_tedarikci: 'fa-exchange-alt'
};

const DealerList = ({ onCustomerChange }) => {
  const { t, i18n } = useTranslation();
  const { user, permissions } = useAuth();
  const [dealers, setDealers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  
  const initialDealerState = {
    companyName: '',
    name: '',
    phone1: '',
    phone2: '',
    email: '',
    address: '',
    notes: '',
    firmType: 'bayi'
  };
  const [newDealer, setNewDealer] = useState(initialDealerState);
  const [editingDealer, setEditingDealer] = useState(null);
  const [isDealerInfoCollapsed, setIsDealerInfoCollapsed] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Tab/Filter states
  const [filterTab, setFilterTab] = useState('all'); // 'all', 'bayi', 'tedarikci', 'bayi_tedarikci'
  const [dealerSubTab, setDealerSubTab] = useState('bayi'); // 'bayi', 'tedarikci'
  
  // Supplier Sessions State
  const [supplierSessions, setSupplierSessions] = useState([]);
  const [isLoadingSupplierSessions, setIsLoadingSupplierSessions] = useState(false);

  // Döviz kurları (tedarikçi TL çevirimi için)
  const exchangeRates = useExchangeRates();
  
  // Sessions State
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isOrderFormOpen, setIsOrderFormOpen] = useState(false); // OrderDetails içinde form açık mı?
  const [selectedSalesUserId, setSelectedSalesUserId] = useState(null); // Seçilen satışçı (null = seçilmedi)
  const [deleteSessionConfirm, setDeleteSessionConfirm] = useState(null); // Silinecek session
  const [sessionPage, setSessionPage] = useState(1);
  const SESSION_PAGE_SIZE = 10;
  const [dealerPdfDateFrom, setDealerPdfDateFrom] = useState('');
  const [dealerPdfDateTo, setDealerPdfDateTo] = useState('');
  const [isGeneratingDealerPdf, setIsGeneratingDealerPdf] = useState(false);

  // Toast
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  }, []);

  const fetchDealers = useCallback(async () => {
    setIsLoading(true);
    try {
      const q = query(collection(db, 'dealers'));
      const snapshot = await getDocs(q);
      const dealersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort client side (newest first)
      dealersData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return dateB.getTime() - dateA.getTime();
      });
      
      setDealers(dealersData);
    } catch (error) {
      console.error("Bayiler yüklenirken hata:", error);
      showToast("Bayiler yüklenirken bir hata oluştu.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchDealers();
  }, [fetchDealers]);

  useEffect(() => {
    setDealerPdfDateFrom('');
    setDealerPdfDateTo('');
  }, [editingDealer?.id]);

  const handleGenerateDealerCombinedCustomerPDF = async () => {
    if (!editingDealer) return;

    const filteredSessions = filterSessionsByDateRange(sessions, dealerPdfDateFrom, dealerPdfDateTo);
    if (filteredSessions.length === 0) {
      showToast(t('dealers.details.combinedPdf.noSessionsInRange'), 'warning');
      return;
    }

    setIsGeneratingDealerPdf(true);
    try {
      const ordersBySession = await fetchOrdersGroupedBySessions(filteredSessions.map((session) => session.id));
      const sessionsWithOrders = filteredSessions
        .map((session) => ({
          session,
          orders: ordersBySession[session.id] || []
        }))
        .filter((entry) => entry.orders.length > 0);

      if (sessionsWithOrders.length === 0) {
        showToast(t('dealers.details.combinedPdf.noOrders'), 'warning');
        return;
      }

      generateDealerCombinedCustomerPDF(editingDealer, sessionsWithOrders, {
        dateFrom: dealerPdfDateFrom,
        dateTo: dealerPdfDateTo
      });
    } catch (error) {
      console.error('Bayi toplu PDF oluşturulurken hata:', error);
      showToast(t('dealers.details.combinedPdf.error'), 'error');
    } finally {
      setIsGeneratingDealerPdf(false);
    }
  };

  const handleGenerateDealerCombinedLabelPDF = async () => {
    if (!editingDealer) return;

    const filteredSessions = filterSessionsByDateRange(sessions, dealerPdfDateFrom, dealerPdfDateTo);
    if (filteredSessions.length === 0) {
      showToast(t('dealers.details.combinedPdf.noSessionsInRange'), 'warning');
      return;
    }

    try {
      await generateDealerCombinedLabelPDF(editingDealer, filteredSessions);
    } catch (error) {
      console.error('Bayi etiket PDF oluşturulurken hata:', error);
      showToast(t('dealers.details.combinedPdf.error'), 'error');
    }
  };

  const filteredPdfSessionCount = filterSessionsByDateRange(sessions, dealerPdfDateFrom, dealerPdfDateTo).length;

  // Bayi İşlemleri
  const handleAddDealer = async (e) => {
    e.preventDefault();
    if (!newDealer.companyName || !newDealer.name || !newDealer.phone1) {
      showToast('Firma Adı, Yetkili Adı ve Telefon zorunludur.', 'warning');
      return;
    }

    try {
      setIsLoading(true);
      const dealerData = {
        ...newDealer,
        firmType: newDealer.firmType || 'bayi',
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || null
      };

      const docRef = await addDoc(collection(db, 'dealers'), dealerData);
      showToast(t('dealers.notifications.addedSuccess'), 'success');
      
      // Local state'i güncelle (okuma maliyetini sıfırlamak için)
      setDealers(prev => [{ id: docRef.id, ...dealerData }, ...prev]);
      setNewDealer(initialDealerState);
      setIsAdding(false);

      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (error) {
      console.error("Bayi eklenirken hata:", error);
      showToast("Hata oluştu: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateDealer = async (e) => {
    e.preventDefault();
    if (!editingDealer.companyName || !editingDealer.name || !editingDealer.phone1) {
      showToast('Firma Adı, Yetkili Adı ve Telefon zorunludur.', 'warning');
      return;
    }

    try {
      setIsLoading(true);
      const updateData = {
        companyName: editingDealer.companyName,
        name: editingDealer.name,
        phone1: editingDealer.phone1,
        phone2: editingDealer.phone2,
        email: editingDealer.email,
        address: editingDealer.address,
        notes: editingDealer.notes,
        firmType: editingDealer.firmType || 'bayi',
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'dealers', editingDealer.id), updateData);
      showToast('Firma bilgileri güncellendi!', 'success');
      
      // Update local state without fetching all
      setDealers(prev => prev.map(d => d.id === editingDealer.id ? { ...d, ...updateData } : d));

      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (error) {
      console.error("Güncelleme hatası:", error);
      showToast("Hata oluştu: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDealer = async () => {
    if (!deleteConfirm) return;
    try {
      setIsLoading(true);
      await deleteDoc(doc(db, 'dealers', deleteConfirm.id));
      showToast('Bayi başarıyla silindi!', 'success');
      setDealers(prev => prev.filter(d => d.id !== deleteConfirm.id));
      setDeleteConfirm(null);
      if (editingDealer?.id === deleteConfirm.id) {
        setEditingDealer(null);
      }

      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (error) {
      console.error("Silme hatası:", error);
      showToast("Hata oluştu: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Sipariş Listesi (Session) Silme
  const handleDeleteSession = async () => {
    if (!deleteSessionConfirm) return;
    setIsLoadingSessions(true);
    try {
      // 1) Bu session'a ait tüm alt siparişleri bul ve sil
      const ordersQuery = query(
        collection(db, 'orders'),
        where('customerId', '==', deleteSessionConfirm.id)
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersData = ordersSnapshot.docs.map((orderDoc) => ({
        id: orderDoc.id,
        ...orderDoc.data()
      }));

      for (const order of ordersData) {
        try {
          if (isTulKanatProductType(order.productType)) {
            await restoreTulKanatStockForOrder(db, order, true); // toptan
          } else if (isQuantityStockProductType(order.productType)) {
            await restoreQuantityStockForOrder(db, order);
          }
        } catch (stockErr) {
          console.error('Stok geri yükleme hatası:', stockErr);
        }
      }

      const deleteOrderPromises = ordersSnapshot.docs.map(orderDoc =>
        deleteDoc(doc(db, 'orders', orderDoc.id))
      );
      await Promise.all(deleteOrderPromises);

      // 2) Session belgesini sil
      await deleteDoc(doc(db, 'dealer_sessions', deleteSessionConfirm.id));

      // 2b) Oturuma ait primleri satışçıdan geri al
      const sessionSalesUserId = deleteSessionConfirm.salesUserId;
      if (sessionSalesUserId && sessionSalesUserId !== 'no-selection') {
        try {
          await reverseCommissionFromUser(db, sessionSalesUserId, deleteSessionConfirm.id);
        } catch (commErr) {
          console.error('Oturum silinirken prim iptali hatası:', commErr);
        }
      }

      // 3) Local state'i güncelle
      setSessions(prev => prev.filter(s => s.id !== deleteSessionConfirm.id));
      setDeleteSessionConfirm(null);
      showToast(t('dealers.details.deleteSessionModal.deleteSuccess', { count: ordersSnapshot.docs.length }), 'success');

      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (error) {
      console.error('Session silme hatası:', error);
      showToast('Silme işlemi başarısız: ' + error.message, 'error');
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const fetchSupplierSessions = useCallback(async (dealerId) => {
    if (!dealerId) return;
    setIsLoadingSupplierSessions(true);
    try {
      const q = query(
        collection(db, 'supplier_sessions'),
        where('dealerId', '==', dealerId)
      );
      const snapshot = await getDocs(q);
      const sessionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      sessionsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return dateB.getTime() - dateA.getTime();
      });

      setSupplierSessions(sessionsData);
    } catch (error) {
      console.error("Tedarikçi oturumları yüklenirken hata:", error);
    } finally {
      setIsLoadingSupplierSessions(false);
    }
  }, []);

  const fetchDealerSessions = useCallback(async (dealerId) => {
    if (!dealerId) return;
    setIsLoadingSessions(true);
    try {
      const q = query(
        collection(db, 'dealer_sessions'),
        where('dealerId', '==', dealerId)
      );
      const snapshot = await getDocs(q);
      const sessionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by descending created date
      sessionsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return dateB.getTime() - dateA.getTime();
      });

      setSessions(sessionsData);
    } catch (error) {
      console.error("Müşteri oturumları yüklenirken hata:", error);
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  const openDealerSession = (session, sessionNameForDetails) => {
    setSelectedSession({
      ...session,
      name: sessionNameForDetails,
      phone1: editingDealer?.phone1,
      address: editingDealer?.address
    });
    setSelectedSalesUserId(session.salesUserId ?? null);
    setIsOrderFormOpen(false);
  };

  const handleCreateSession = async () => {
    if (!editingDealer) return;
    try {
      setIsLoadingSessions(true);
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`; // YYYY-MM-DD (Yerel Saat Dilimine Duyarlı)
      
      const sessionData = {
        dealerId: editingDealer.id,
        dealerName: editingDealer.companyName,
        sessionDate: dateStr,
        orderDate: dateStr,
        deliveryDate: '',
        orderStatus: ORDER_STATUS.NEW_ORDER,
        paidAmount: 0,
        isPaymentCompleted: false,
        remainingAmount: 0,
        totalOrderAmount: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || null
      };

      const docRef = await addDoc(collection(db, 'dealer_sessions'), sessionData);
      const newSession = { id: docRef.id, ...sessionData };
      setSessions(prev => [newSession, ...prev]);
      showToast("Yeni sipariş listesi hazır. Şimdi ürünlerinizi ekleyebilirsiniz.", "success");

      if (onCustomerChange) {
        onCustomerChange();
      }
      
      // Yeni oluşturulan oturumu doğrudan aç
      const sessionNameForDetails = `${editingDealer.companyName} (${formatLocalizedDate(new Date(), i18n.language)})`;
      openDealerSession(newSession, sessionNameForDetails);
      setSelectedSalesUserId(null);

    } catch (error) {
      console.error("Oturum oluşturulurken hata:", error);
      showToast("Sipariş listesi oluşturulamadı.", "error");
    } finally {
      setIsLoadingSessions(false);
    }
  };

  // Sipariş eklenince/silinince session bakiyesini Firestore'da güncelle
  const handleSyncSessionBalance = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      // Bu session'a ait tüm siparişleri Firestore'dan doğrudan çek (en güncel veri)
      const ordersQ = query(collection(db, 'orders'), where('customerId', '==', sessionId));
      const ordersSnap = await getDocs(ordersQ);
      const ordersData = ordersSnap.docs.map(d => d.data());

      // Toplam sipariş tutarını hesapla
      const totalOrderAmount = ordersData.reduce((sum, order) => {
        // Hiyerarşi: Ürün indirimli fiyatı (discountedPrice) > Normal fiyat (totalPrice)
        const price = (order.discountedPrice && parseFloat(order.discount || 0) > 0)
          ? parseFloat(order.discountedPrice || 0)
          : parseFloat(order.totalPrice || 0);
        return sum + price;
      }, 0);

      // Session dökümanını Firestore'da güncelle
      const sessionRef = doc(db, 'dealer_sessions', sessionId);
      
      // Ödenen tutarı mevcut state'den al (veya Firestore'dan çekebiliriz ama state genelde günceldir)
      // Daha güvenli olması için Firestore'dan çekelim
      const sessionSnap = await getDocs(query(collection(db, 'dealer_sessions'), where('__name__', '==', sessionId)));
      if (sessionSnap.empty) return;
      
      const sessionFirestoreData = sessionSnap.docs[0].data();
      const currentPaid = parseFloat(sessionFirestoreData.paidAmount || 0);

      // Manuel override varsa totalOrderAmount'ı override değeriyle koru, yalnızca remainingAmount güncelle
      const hasManualOverride = sessionFirestoreData.manualTotalOverride != null;
      const effectiveTotalForSession = hasManualOverride
        ? parseFloat(sessionFirestoreData.manualTotalOverride)
        : totalOrderAmount;
      const newRemaining = Math.max(0, effectiveTotalForSession - currentPaid);

      await updateDoc(sessionRef, {
        ...(hasManualOverride ? {} : { totalOrderAmount }),
        remainingAmount: newRemaining,
        updatedAt: new Date()
      });

      // Local state'i güncelle
      setSessions(prev => prev.map(s => 
        s.id === sessionId 
          ? { ...s, ...(hasManualOverride ? {} : { totalOrderAmount: effectiveTotalForSession }), remainingAmount: newRemaining }
          : s
      ));

      // Dashboard önbelleğini ve istatistiklerini temizleyip yenile
      if (onCustomerChange) {
        onCustomerChange();
      }
      
      console.log(`Bakiye senkronize edildi: ${sessionId} - Toplam: ${totalOrderAmount}`);
    } catch (error) {
      console.error('Session bakiyesi güncellenirken hata:', error);
    }
  }, [onCustomerChange]);

  const handleEditDealerClick = (dealer) => {
    if (editingDealer?.id === dealer.id) {
      setEditingDealer(null); // Toggle close
    } else {
      setEditingDealer(dealer);
      setIsAdding(false);
      setSessionPage(1); // Sayfa başına dön
      
      const type = dealer.firmType || 'bayi';
      if (type === 'bayi') {
        fetchDealerSessions(dealer.id);
        setDealerSubTab('bayi');
      } else if (type === 'tedarikci') {
        fetchSupplierSessions(dealer.id);
        setDealerSubTab('tedarikci');
      } else if (type === 'bayi_tedarikci') {
        fetchDealerSessions(dealer.id);
        fetchSupplierSessions(dealer.id);
        setDealerSubTab('bayi');
      }
      
      // Scroll to top or specific element
      setTimeout(() => {
        window.scrollTo({ top: 300, behavior: 'smooth' });
      }, 100);
    }
  };

  const handleSessionPaymentSaved = useCallback(() => {
    if (editingDealer) {
      fetchDealerSessions(editingDealer.id);
    }
    // Dashboard önbelleğini ve istatistiklerini temizleyip yenile
    if (onCustomerChange) {
      onCustomerChange();
    }
  }, [editingDealer, fetchDealerSessions, onCustomerChange]);

  const filteredDealers = dealers.filter(d => {
    if (filterTab === 'all') return true;
    return (d.firmType || 'bayi') === filterTab;
  });

  return (
    <div className="dealer-list-container">
      {toast.show && (
        <div className={`toast-notification ${toast.type}`}>
          <div className="toast-content">
            <div className="toast-icon">
              {toast.type === 'success' && <i className="fas fa-check"></i>}
              {toast.type === 'error' && <i className="fas fa-exclamation-triangle"></i>}
              {toast.type === 'warning' && <i className="fas fa-exclamation-circle"></i>}
            </div>
            <div className="toast-message">{toast.message}</div>
            <button className="toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="toast-progress"></div>
        </div>
      )}

      <div className="dealer-header">
        <h2>
          <i className="fas fa-store"></i> {t('dealers.title')}
        </h2>
        <button 
          className="add-dealer-btn"
          onClick={() => {
            setIsAdding(!isAdding);
            setEditingDealer(null);
            setNewDealer(initialDealerState);
          }}
          disabled={isLoading}
        >
          {isAdding ? (
            <><i className="fas fa-times"></i> {t('common.cancel')}</>
          ) : (
            <><i className="fas fa-plus"></i> {t('dealers.addNew')}</>
          )}
        </button>
      </div>

      {isAdding && (
        <div className="add-dealer-form">
          <h3>{t('dealers.form.createTitle')}</h3>
          <form onSubmit={handleAddDealer}>
            <div className="dealer-info-section">
              <div className="form-grid">
                <div className="form-group">
                  <label><i className="fas fa-building"></i>{t('dealers.form.companyName')}</label>
                  <input
                    type="text"
                    value={newDealer.companyName}
                    onChange={(e) => setNewDealer({...newDealer, companyName: e.target.value})}
                    placeholder={t('dealers.form.placeholders.companyName')}
                    required
                  />
                </div>
                <div className="form-group">
                  <label><i className="fas fa-user-tie"></i>{t('dealers.form.authorizedPerson')}</label>
                  <input
                    type="text"
                    value={newDealer.name}
                    onChange={(e) => setNewDealer({...newDealer, name: e.target.value})}
                    placeholder={t('dealers.form.placeholders.authorizedPerson')}
                    required
                  />
                </div>
                <div className="form-group">
                  <label><i className="fas fa-envelope"></i>{t('dealers.form.email')}</label>
                  <input
                    type="email"
                    value={newDealer.email}
                    onChange={(e) => setNewDealer({...newDealer, email: e.target.value})}
                    placeholder={t('dealers.form.placeholders.email')}
                  />
                </div>
                
                <div className="form-group">
                  <label><i className="fas fa-phone"></i>{t('dealers.form.phone1')}</label>
                  <input
                    type="tel"
                    value={newDealer.phone1}
                    onChange={(e) => setNewDealer({ ...newDealer, phone1: sanitizePhoneInput(e.target.value) })}
                    placeholder={t('dealers.form.placeholders.phone')}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label><i className="fas fa-phone-alt"></i>{t('dealers.form.phone2')}</label>
                  <input
                    type="tel"
                    value={newDealer.phone2}
                    onChange={(e) => setNewDealer({ ...newDealer, phone2: sanitizePhoneInput(e.target.value) })}
                    placeholder={t('dealers.form.placeholders.phone')}
                  />
                </div>

                <div className="form-group">
                  <label><i className="fas fa-map-marker-alt"></i>{t('dealers.form.address')}</label>
                  <input
                    type="text"
                    value={newDealer.address}
                    onChange={(e) => setNewDealer({...newDealer, address: e.target.value})}
                    placeholder={t('dealers.form.placeholders.address')}
                  />
                </div>

                <div className="form-group">
                  <label><i className="fas fa-tags"></i>{t('dealers.form.firmType')}</label>
                  <select
                    value={newDealer.firmType || 'bayi'}
                    onChange={(e) => setNewDealer({...newDealer, firmType: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      fontSize: '1.05rem',
                      background: '#f8fafc',
                      color: '#1e293b'
                    }}
                  >
                    {FIRM_TYPE_OPTIONS.map((type) => (
                      <option key={type} value={type}>{t(`dealers.form.firmTypes.${type}`)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="save-btn" disabled={isLoading}>
                {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>} {t('common.save')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Editing Dealer Form & Sessions */}
      {editingDealer && (
        <div className="add-dealer-form">
          <div
            className="section-header collapsible"
            onClick={() => setIsDealerInfoCollapsed(!isDealerInfoCollapsed)}
            style={{ marginBottom: '15px' }}
          >
            <h4>
              <i className="fas fa-building" style={{ marginRight: '10px', color: '#3b82f6' }}></i>
              {t('dealers.details.title', { name: editingDealer.companyName })}
            </h4>
            <i className={`fas fa-chevron-${isDealerInfoCollapsed ? 'down' : 'up'}`}></i>
          </div>

          <div className={`section-content ${isDealerInfoCollapsed ? 'collapsed' : ''}`}>
            <form onSubmit={handleUpdateDealer}>
              <div className="dealer-info-section">
              <div className="form-grid">
                <div className="form-group">
                  <label><i className="fas fa-building"></i>{t('dealers.form.companyName')}</label>
                  <input
                    type="text"
                    value={editingDealer.companyName || ''}
                    onChange={(e) => setEditingDealer({...editingDealer, companyName: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label><i className="fas fa-user-tie"></i>{t('dealers.form.authorizedPerson')}</label>
                  <input
                    type="text"
                    value={editingDealer.name || ''}
                    onChange={(e) => setEditingDealer({...editingDealer, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label><i className="fas fa-envelope"></i>{t('dealers.form.email')}</label>
                  <input
                    type="email"
                    value={editingDealer.email || ''}
                    onChange={(e) => setEditingDealer({...editingDealer, email: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label><i className="fas fa-phone"></i>{t('dealers.form.phone1')}</label>
                  <input
                    type="tel"
                    value={editingDealer.phone1 || ''}
                    onChange={(e) => setEditingDealer({ ...editingDealer, phone1: sanitizePhoneInput(e.target.value) })}
                    placeholder={t('dealers.form.placeholders.phone')}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label><i className="fas fa-phone-alt"></i>{t('dealers.form.phone2')}</label>
                  <input
                    type="tel"
                    value={editingDealer.phone2 || ''}
                    onChange={(e) => setEditingDealer({ ...editingDealer, phone2: sanitizePhoneInput(e.target.value) })}
                    placeholder={t('dealers.form.placeholders.phone')}
                  />
                </div>

                <div className="form-group">
                  <label><i className="fas fa-map-marker-alt"></i>{t('dealers.form.address')}</label>
                  <input
                    type="text"
                    value={editingDealer.address || ''}
                    onChange={(e) => setEditingDealer({...editingDealer, address: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label><i className="fas fa-tags"></i>{t('dealers.form.firmType')}</label>
                  <select
                    value={editingDealer.firmType || 'bayi'}
                    onChange={(e) => setEditingDealer({...editingDealer, firmType: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      fontSize: '1.05rem',
                      background: '#f8fafc',
                      color: '#1e293b'
                    }}
                  >
                    {FIRM_TYPE_OPTIONS.map((type) => (
                      <option key={type} value={type}>{t(`dealers.form.firmTypes.${type}`)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="form-actions" style={{ marginTop: '10px' }}>
              <button type="submit" className="save-btn" disabled={isLoading}>
                {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-sync-alt"></i>} {t('dealers.form.update')}
              </button>
              <button type="button" className="cancel-btn" onClick={() => setEditingDealer(null)}>
                {t('common.close')}
              </button>
            </div>
          </form>
        </div>

          {/* Tabs for bayi_tedarikci */}
          {(editingDealer.firmType || 'bayi') === 'bayi_tedarikci' && (
            <div className="dealer-detail-tabs" style={{ display: 'flex', gap: '10px', marginTop: '20px', borderBottom: '2px solid #e2e8f0', paddingBottom: '10px' }}>
              <button
                type="button"
                className={`detail-tab-btn ${dealerSubTab === 'bayi' ? 'active' : ''}`}
                onClick={() => setDealerSubTab('bayi')}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: dealerSubTab === 'bayi' ? '#3b82f6' : '#f1f5f9',
                  color: dealerSubTab === 'bayi' ? 'white' : '#475569'
                }}
              >
                <i className="fas fa-store"></i> {t('dealers.tabs.dealerOrders')}
              </button>
              <button
                type="button"
                className={`detail-tab-btn ${dealerSubTab === 'tedarikci' ? 'active' : ''}`}
                onClick={() => setDealerSubTab('tedarikci')}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: dealerSubTab === 'tedarikci' ? '#8b5cf6' : '#f1f5f9',
                  color: dealerSubTab === 'tedarikci' ? 'white' : '#475569'
                }}
              >
                <i className="fas fa-truck"></i> {t('dealers.tabs.supplierPurchases')}
              </button>
            </div>
          )}

          {/* Conditional Rendering of Panels */}
          {((editingDealer.firmType || 'bayi') === 'bayi' || 
            ((editingDealer.firmType || 'bayi') === 'bayi_tedarikci' && dealerSubTab === 'bayi')) && (
            <div className="dealer-sessions-section">
              <div className="sessions-header">
                <h4>
                  <i className="fas fa-clipboard-list"></i> {t('dealers.details.orderLists')}
                </h4>
                <button className="add-session-btn" onClick={handleCreateSession} disabled={isLoadingSessions}>
                  <i className="fas fa-plus-circle"></i> {t('dealers.details.newOrder')}
                </button>
              </div>

              {/* Genel Cari Özet Kartları */}
              {(() => {
                const grandTotal = sessions.reduce((s, ss) => s + parseFloat(ss.totalOrderAmount || 0), 0);
                const grandPaid = sessions.reduce((s, ss) => s + parseFloat(ss.paidAmount || 0), 0);
                const grandRemaining = sessions.reduce((s, ss) => s + parseFloat(ss.remainingAmount ?? Math.max(0, parseFloat(ss.totalOrderAmount || 0) - parseFloat(ss.paidAmount || 0))), 0);
                const fmt = (v) => new Intl.NumberFormat('ru-RU', { style: 'currency', currency: CURRENCY_CODE }).format(v);
                
                // Tedarikçi borçlarını tengeye çevir (farklı kur ise anlık kurdan)
                const grandSupplierRemaining = supplierSessions.reduce((s, ss) => {
                  const remaining = parseFloat(ss.remainingAmount ?? Math.max(0, parseFloat(ss.totalOrderAmount || 0) - parseFloat(ss.paidAmount || 0)));
                  const cur = ss.currency || 'KZT';
                  if (isLocalCurrency(cur)) return s + remaining;
                  const rate = parseFloat(exchangeRates[cur]) || 1;
                  return s + remaining * rate;
                }, 0);
                const netBalance = grandRemaining - grandSupplierRemaining;

                return (
                  <div>
                    {sessions.length > 0 && (
                      <div className="dealer-financial-summary">
                        <div className="financial-card total-card">
                          <div className="financial-card-icon"><i className="fas fa-file-invoice-dollar"></i></div>
                          <div className="financial-card-info">
                            <span className="financial-card-label">{t('dealers.details.financial.totalOrderAmount')}</span>
                            <span className="financial-card-value">{fmt(grandTotal)}</span>
                          </div>
                        </div>
                        <div className="financial-card paid-card">
                          <div className="financial-card-icon"><i className="fas fa-check-circle"></i></div>
                          <div className="financial-card-info">
                            <span className="financial-card-label">{t('dealers.details.financial.collected')}</span>
                            <span className="financial-card-value">{fmt(grandPaid)}</span>
                          </div>
                        </div>
                        <div className="financial-card remaining-card">
                          <div className="financial-card-icon"><i className="fas fa-hourglass-half"></i></div>
                          <div className="financial-card-info">
                            <span className="financial-card-label">{t('dealers.details.financial.openBalance')}</span>
                            <span className="financial-card-value">{fmt(grandRemaining)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Net Bakiye Kartı (sadece hem bayi hem tedarikçiyse) */}
                    {(editingDealer.firmType || 'bayi') === 'bayi_tedarikci' && (
                      <div className="net-balance-section" style={{
                        marginTop: '15px',
                        marginBottom: '20px',
                        padding: '20px',
                        background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
                        borderRadius: '16px',
                        border: '1.5px solid #d8b4fe',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        boxShadow: '0 4px 12px rgba(139, 92, 246, 0.08)'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#6b21a8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            <i className="fas fa-balance-scale"></i> Net Cari Bakiye Durumu
                          </span>
                          <span style={{ fontSize: '0.95rem', color: '#581c87', fontWeight: 500 }}>
                            Bayinin Borcu ({fmt(grandRemaining)}) - Tedarikçiye Borcumuz ({fmt(grandSupplierRemaining)} ₺)
                          </span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{
                            fontSize: '1.35rem',
                            fontWeight: 800,
                            color: netBalance > 0 ? '#166534' : netBalance < 0 ? '#dc2626' : '#475569',
                            fontFamily: 'Inter, monospace'
                          }}>
                            {netBalance > 0 ? `Net Alacağımız: ${fmt(netBalance)}` : netBalance < 0 ? `Net Borcumuz: ${fmt(Math.abs(netBalance))}` : `Dengede: ${fmt(0)}`}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {isLoadingSessions ? (
                <div className="dealer-loading-container">
                  <i className="fas fa-spinner fa-spin fa-2x" style={{ color: '#94a3b8' }}></i>
                  <p style={{ color: '#64748b', marginTop: '10px' }}>{t('dealers.details.loadingSessions')}</p>
                </div>
              ) : sessions.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-box-open"></i>
                  <p>{t('dealers.details.noOrders')}</p>
                </div>
              ) : (
                <table className="sessions-table">
                  <thead>
                    <tr>
                      <th>{t('dealers.details.table.orderDate')}</th>
                      <th>{t('dealers.details.table.deliveryDate')}</th>
                      <th>{t('dealers.details.table.status')}</th>
                      <th>{t('dealers.details.table.totalBalance')}</th>
                      <th>{t('dealers.details.table.paidRemaining')}</th>
                      <th>{t('dealers.details.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions
                      .slice((sessionPage - 1) * SESSION_PAGE_SIZE, sessionPage * SESSION_PAGE_SIZE)
                      .map(session => {
                      const totalOrderAmt = parseFloat(session.totalOrderAmount || 0);
                      const paid = parseFloat(session.paidAmount || 0);
                      const remaining = parseFloat(session.remainingAmount ?? (totalOrderAmt - paid));
                      const status = session.orderStatus || DEALER_STATUS.NEW_ORDER;
                      const statusClass = DEALER_STATUS_CLASS[status] || 'status-new';

                      // Sipariş tarihi: önce orderDate, yoksa sessionDate, yoksa createdAt
                      const displayDateStr = session.orderDate || session.sessionDate || null;
                      const sessionDisplayDate = displayDateStr
                        ? new Date(displayDateStr + 'T00:00:00')
                        : (session.createdAt?.toDate ? session.createdAt.toDate() : new Date());
                      const sessionNameForDetails = `${editingDealer.companyName} (${formatLocalizedDate(sessionDisplayDate, i18n.language)})`;

                      return (
                        <tr key={session.id}>
                          <td className="session-date">
                            {formatLocalizedLongDate(sessionDisplayDate, i18n.language)}
                          </td>
                          <td className="session-date" style={{ color: session.deliveryDate ? '#059669' : '#94a3b8', fontStyle: session.deliveryDate ? 'normal' : 'italic' }}>
                            {session.deliveryDate
                              ? formatLocalizedLongDate(session.deliveryDate + 'T00:00:00', i18n.language)
                              : '—'}
                          </td>
                          <td>
                            <select
                              className={`session-status-select ${statusClass}`}
                              value={status}
                              onChange={async (e) => {
                                const newStatus = e.target.value;
                                try {
                                  await updateDoc(doc(db, 'dealer_sessions', session.id), {
                                    orderStatus: newStatus,
                                    updatedAt: new Date()
                                  });
                                  setSessions(prev => prev.map(s =>
                                    s.id === session.id ? { ...s, orderStatus: newStatus } : s
                                  ));
                                } catch (err) {
                                  console.error('Durum güncellenemedi:', err);
                                }
                              }}
                            >
                              {DEALER_STATUS_KEYS.map((key) => (
                                <option key={key} value={key}>{t(`dealers.orderStatus.${key}`)}</option>
                              ))}
                            </select>
                          </td>
                          <td className="finance-cell text-gray">
                            {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: CURRENCY_CODE }).format(totalOrderAmt || (remaining + paid))}
                          </td>
                          <td className="finance-cell">
                            <span className="text-green">{new Intl.NumberFormat('ru-RU', { style: 'currency', currency: CURRENCY_CODE }).format(paid)}</span>
                            <span style={{color: '#cbd5e1', margin: '0 8px'}}>|</span>
                            <span className="text-red">{new Intl.NumberFormat('ru-RU', { style: 'currency', currency: CURRENCY_CODE }).format(remaining)}</span>
                          </td>
                          <td className="session-actions">
                            <button 
                              type="button"
                              className="btn-detail"
                              onClick={() => openDealerSession(session, sessionNameForDetails)}
                            >
                              <i className="fas fa-external-link-alt"></i> {t('dealers.details.viewOrders')}
                            </button>
                            <button
                              type="button"
                              className="btn-delete-session"
                              title={t('dealers.details.deleteSessionTitle')}
                              onClick={() => setDeleteSessionConfirm(session)}
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Pagination */}
              {sessions.length > SESSION_PAGE_SIZE && (
                <div className="sessions-pagination">
                  <button
                    type="button"
                    className="page-btn"
                    onClick={() => setSessionPage(p => Math.max(1, p - 1))}
                    disabled={sessionPage === 1}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>

                  {Array.from({ length: Math.ceil(sessions.length / SESSION_PAGE_SIZE) }, (_, i) => i + 1).map(page => (
                    <button
                      type="button"
                      key={page}
                      className={`page-btn ${sessionPage === page ? 'active' : ''}`}
                      onClick={() => setSessionPage(page)}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    type="button"
                    className="page-btn"
                    onClick={() => setSessionPage(p => Math.min(Math.ceil(sessions.length / SESSION_PAGE_SIZE), p + 1))}
                    disabled={sessionPage === Math.ceil(sessions.length / SESSION_PAGE_SIZE)}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>

                  <span className="page-info">
                    {t('dealers.details.pagination', {
                      from: (sessionPage - 1) * SESSION_PAGE_SIZE + 1,
                      to: Math.min(sessionPage * SESSION_PAGE_SIZE, sessions.length),
                      total: sessions.length
                    })}
                  </span>
                </div>
              )}

              {sessions.length > 0 && (
                <div className="dealer-combined-pdf-bar">
                  <div className="dealer-combined-pdf-info">
                    <h5>
                      <i className="fas fa-file-pdf"></i>
                      {t('dealers.details.combinedPdf.title')}
                    </h5>
                    <p>{t('dealers.details.combinedPdf.description')}</p>
                  </div>

                  <div className="dealer-combined-pdf-controls">
                    <div className="dealer-combined-pdf-dates">
                      <label>
                        <span>{t('dealers.details.combinedPdf.dateFrom')}</span>
                        <input
                          type="date"
                          value={dealerPdfDateFrom}
                          onChange={(e) => setDealerPdfDateFrom(e.target.value)}
                          disabled={isGeneratingDealerPdf}
                        />
                      </label>
                      <label>
                        <span>{t('dealers.details.combinedPdf.dateTo')}</span>
                        <input
                          type="date"
                          value={dealerPdfDateTo}
                          onChange={(e) => setDealerPdfDateTo(e.target.value)}
                          disabled={isGeneratingDealerPdf}
                        />
                      </label>
                      {(dealerPdfDateFrom || dealerPdfDateTo) && (
                        <button
                          type="button"
                          className="dealer-pdf-clear-dates"
                          onClick={() => {
                            setDealerPdfDateFrom('');
                            setDealerPdfDateTo('');
                          }}
                          disabled={isGeneratingDealerPdf}
                        >
                          <i className="fas fa-times"></i>
                          {t('dealers.details.combinedPdf.clearDates')}
                        </button>
                      )}
                    </div>

                    <div className="dealer-combined-pdf-actions">
                      <span className="dealer-combined-pdf-count">
                        {t('dealers.details.combinedPdf.sessionCount', { count: filteredPdfSessionCount })}
                      </span>
                      <button
                        type="button"
                        className="dealer-combined-pdf-btn"
                        onClick={handleGenerateDealerCombinedCustomerPDF}
                        disabled={isGeneratingDealerPdf || filteredPdfSessionCount === 0}
                        title={t('orders.pdf.customerTitle')}
                      >
                        {isGeneratingDealerPdf ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            {t('dealers.details.combinedPdf.generating')}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-file-pdf"></i>
                            {t('orders.pdf.customer')}
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="dealer-combined-pdf-btn dealer-combined-label-btn"
                        onClick={handleGenerateDealerCombinedLabelPDF}
                        disabled={isGeneratingDealerPdf || filteredPdfSessionCount === 0}
                        title={t('orders.pdf.labelTitle')}
                      >
                        <i className="fas fa-tag"></i>
                        {t('orders.pdf.label')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {(((editingDealer.firmType || 'bayi') === 'tedarikci') || 
            ((editingDealer.firmType || 'bayi') === 'bayi_tedarikci' && dealerSubTab === 'tedarikci')) && (
            <SupplierSessionPanel
              dealer={editingDealer}
              db={db}
              user={user}
              sessions={supplierSessions}
              setSessions={setSupplierSessions}
              isLoading={isLoadingSupplierSessions}
              fetchSessions={fetchSupplierSessions}
              onCustomerChange={onCustomerChange}
            />
          )}

          {/* Bottom Action Footer for Editing/Details view */}
          <div className="detail-footer-actions" style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '30px',
            paddingTop: '20px',
            borderTop: '2px solid #e2e8f0',
            width: '100%'
          }}>
            <button
              type="button"
              className="cancel-btn"
              onClick={() => {
                setEditingDealer(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 36px',
                borderRadius: '12px',
                fontSize: '1.05rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
              }}
            >
              <i className="fas fa-times"></i>
              {t('dealers.details.closeDetails')}
            </button>
          </div>
        </div>
      )}


      {/* Main Dealer List */}
      {!editingDealer && !isAdding && (
        <div className="dealer-filter-tabs">
          <button 
            type="button"
            className={`filter-tab-btn ${filterTab === 'all' ? 'active' : ''}`}
            onClick={() => setFilterTab('all')}
          >
            {t('dealers.filters.all', { count: dealers.length })}
          </button>
          <button 
            type="button"
            className={`filter-tab-btn ${filterTab === 'bayi' ? 'active' : ''}`}
            onClick={() => setFilterTab('bayi')}
          >
            <i className="fas fa-store" style={{ color: '#3b82f6', marginRight: '6px' }}></i>
            {t('dealers.filters.dealers', { count: dealers.filter(d => (d.firmType || 'bayi') === 'bayi').length })}
          </button>
          <button 
            type="button"
            className={`filter-tab-btn ${filterTab === 'tedarikci' ? 'active' : ''}`}
            onClick={() => setFilterTab('tedarikci')}
          >
            <i className="fas fa-truck" style={{ color: '#f97316', marginRight: '6px' }}></i>
            {t('dealers.filters.suppliers', { count: dealers.filter(d => d.firmType === 'tedarikci').length })}
          </button>
          <button 
            type="button"
            className={`filter-tab-btn ${filterTab === 'bayi_tedarikci' ? 'active' : ''}`}
            onClick={() => setFilterTab('bayi_tedarikci')}
          >
            <i className="fas fa-exchange-alt" style={{ color: '#8b5cf6', marginRight: '6px' }}></i>
            {t('dealers.filters.both', { count: dealers.filter(d => d.firmType === 'bayi_tedarikci').length })}
          </button>
        </div>
      )}

      <div className="dealers-table-wrapper">
        {isLoading && !isAdding && !editingDealer && dealers.length === 0 ? (
          <div className="dealer-loading-container">
            <i className="fas fa-spinner fa-spin fa-3x" style={{ color: '#4e6960' }}></i>
          </div>
        ) : filteredDealers.length === 0 ? (
          <div className="empty-state" style={{ padding: '80px 20px' }}>
            <i className="fas fa-store-slash" style={{ fontSize: '4rem', color: '#cbd5e1' }}></i>
            <h3 style={{ fontSize: '1.5rem', marginTop: '20px' }}>{t('dealers.empty.noMatch')}</h3>
            <p>{t('dealers.empty.noMatchHint')}</p>
          </div>
        ) : (
          <table className="dealers-table">
            <thead>
              <tr>
                <th>{t('dealers.table.companyName')}</th>
                <th>{t('dealers.table.firmType')}</th>
                <th>{t('dealers.table.authorized')}</th>
                <th>{t('dealers.table.phone')}</th>
                <th>{t('dealers.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredDealers.map(dealer => {
                const type = dealer.firmType || 'bayi';
                return (
                  <tr key={dealer.id} className="dealer-row-item">
                    <td className="dealer-name-cell">{dealer.companyName}</td>
                    <td>
                      <span className={`firm-type-badge ${type}`}>
                        <i className={`fas ${FIRM_TYPE_ICONS[type] || FIRM_TYPE_ICONS.bayi}`}></i>
                        {t(`dealers.table.firmTypes.${type}`)}
                      </span>
                    </td>
                    <td className="dealer-company-cell">{dealer.name}</td>
                    <td style={{ fontWeight: 500 }}>{dealer.phone1}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          type="button"
                          className="edit-btn" 
                          title={t('dealers.table.editTitle')}
                          onClick={() => handleEditDealerClick(dealer)}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button 
                          type="button"
                          className="delete-btn" 
                          title={t('dealers.table.deleteTitle')}
                          onClick={() => setDeleteConfirm(dealer)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3><i className="fas fa-exclamation-triangle" style={{ color: '#f44336', marginRight: '10px' }}></i> Bayiyi Sil</h3>
            <p><strong>{deleteConfirm.companyName}</strong> adlı bayiyi silmek istediğinizden emin misiniz?</p>
            <p className="warning-text">Bu işlem geri alınamaz.</p>
            <div className="modal-actions">
              <button 
                className="delete-confirm-btn" 
                onClick={handleDeleteDealer}
                disabled={isLoading}
              >
                {isLoading ? <i className="fas fa-spinner fa-spin"></i> : "Evet, Sil"}
              </button>
              <button 
                className="cancel-btn" 
                onClick={() => setDeleteConfirm(null)}
                disabled={isLoading}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sipariş Listesi Silme Onay Modalı */}
      {deleteSessionConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>
              <i className="fas fa-exclamation-triangle" style={{ color: '#f44336', marginRight: '10px' }}></i>
              {t('dealers.details.deleteSessionModal.title')}
            </h3>
            <p>
              <Trans
                i18nKey="dealers.details.deleteSessionModal.confirm"
                values={{
                  date: deleteSessionConfirm.createdAt
                    ? formatLocalizedLongDate(
                        deleteSessionConfirm.createdAt?.toDate
                          ? deleteSessionConfirm.createdAt.toDate()
                          : deleteSessionConfirm.createdAt,
                        i18n.language
                      )
                    : deleteSessionConfirm.sessionDate
                }}
                components={{ strong: <strong /> }}
              />
            </p>
            <p className="warning-text">
              <i className="fas fa-exclamation-circle" style={{ marginRight: '6px' }}></i>
              {t('dealers.details.deleteSessionModal.warning')}
            </p>
            <div className="modal-actions">
              <button
                className="delete-confirm-btn"
                onClick={handleDeleteSession}
                disabled={isLoadingSessions}
              >
                {isLoadingSessions ? <i className="fas fa-spinner fa-spin"></i> : t('dealers.details.deleteSessionModal.yesDelete')}
              </button>
              <button
                className="cancel-btn"
                onClick={() => setDeleteSessionConfirm(null)}
                disabled={isLoadingSessions}
              >
                {t('dealers.details.deleteSessionModal.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ORDER DETAILS OVERLAY */}
      {selectedSession && (
        <div className="order-details-modal-overlay">
          <div className="order-details-modal-content">
            <div className="order-details-modal-header">
              <h3>
                <i className="fas fa-shopping-cart" style={{ color: 'var(--primary-600)', marginRight: '10px' }}></i>
                {selectedSession.name} - Sipariş Yönetimi
              </h3>
              <button className="close-modal-btn" onClick={() => {
                // Modal kapanırken session listesini tab'dan taze çek
                if (editingDealer) {
                  fetchDealerSessions(editingDealer.id);
                }
                setSelectedSession(null);
                setIsOrderFormOpen(false);
              }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="order-details-modal-body">
              <OrderDetails
                customerId={selectedSession.id}
                customerName={selectedSession.name}
                customerInfo={selectedSession}
                isLoading={false}
                orderStatus={selectedSession.orderStatus}
                permissions={permissions}
                parentCollectionName="dealer_sessions"
                onCustomerSaved={handleSessionPaymentSaved}
                onSaveVadeliSuccess={handleSessionPaymentSaved}
                onOrderSaved={() => handleSyncSessionBalance(selectedSession.id)}
                isWholesale={true}
                isNewCustomerWizard={true}
                selectedSalesUserId={selectedSalesUserId}
                onSalesUserChange={(userId) => setSelectedSalesUserId(userId)}
                onFormStateChange={(isOpen) => setIsOrderFormOpen(isOpen)}
                onSessionDateChange={(sessionId, field, value) => {
                  // Sessions tablosunu güncelle
                  setSessions(prev => prev.map(s =>
                    s.id === sessionId ? { ...s, [field]: value } : s
                  ));
                  // selectedSession'u da güncelle (başlığ tarihi için)
                  setSelectedSession(prev => {
                    if (!prev || prev.id !== sessionId) return prev;
                    const updated = { ...prev, [field]: value };
                    // Başlık adını yeniden hesapla
                    if (field === 'orderDate') {
                      const d = new Date(value + 'T00:00:00');
                      updated.name = `${editingDealer.companyName} (${formatLocalizedDate(d, i18n.language)})`;
                    }
                    return updated;
                  });
                  // Dashboard önbelleğini ve istatistiklerini temizleyip yenile
                  if (onCustomerChange) {
                    onCustomerChange();
                  }
                }}
                onOrderStatusChange={async (newStatus) => {
                  try {
                    await updateDoc(doc(db, 'dealer_sessions', selectedSession.id), {
                      orderStatus: newStatus,
                      updatedAt: new Date()
                    });
                    setSelectedSession(prev => ({ ...prev, orderStatus: newStatus }));
                    setSessions(prev => prev.map(s =>
                      s.id === selectedSession.id ? { ...s, orderStatus: newStatus } : s
                    ));
                    // Dashboard önbelleğini ve istatistiklerini temizleyip yenile
                    if (onCustomerChange) {
                      onCustomerChange();
                    }
                  } catch (err) {
                    console.error('Durum güncellenemedi:', err);
                  }
                }}
              />
            </div>
            <div className="order-details-modal-footer" style={{ gap: '10px' }}>
              {isOrderFormOpen && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  background: '#fef3c7', border: '1px solid #f59e0b',
                  borderRadius: '8px', padding: '8px 14px',
                  color: '#92400e', fontSize: '13px', fontWeight: '500'
                }}>
                  <i className="fas fa-exclamation-triangle" style={{ color: '#f59e0b' }}></i>
                  {t('dealers.session.openFormWarning')}
                </div>
              )}
              {!isOrderFormOpen && (selectedSalesUserId ?? selectedSession.salesUserId) == null && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  background: '#fef3c7', border: '1px solid #f59e0b',
                  borderRadius: '8px', padding: '8px 14px',
                  color: '#92400e', fontSize: '13px', fontWeight: '500'
                }}>
                  <i className="fas fa-user-tie" style={{ color: '#f59e0b' }}></i>
                  {t('orders.details.salesperson.required')}
                </div>
              )}
              <button
                className="complete-session-btn"
                disabled={isOrderFormOpen || (selectedSalesUserId ?? selectedSession.salesUserId) == null}
                title={
                  isOrderFormOpen
                    ? t('dealers.session.openFormTitle')
                    : (selectedSalesUserId ?? selectedSession.salesUserId) == null
                      ? t('orders.details.salesperson.required')
                      : ''
                }
                style={(isOrderFormOpen || (selectedSalesUserId ?? selectedSession.salesUserId) == null) ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
                onClick={async () => {
                  const salesUserIdToSave = selectedSalesUserId ?? selectedSession.salesUserId;
                  if (isOrderFormOpen || salesUserIdToSave == null) return;
                  // Prim uygula
                  if (
                    salesUserIdToSave !== 'no-selection' &&
                    selectedSession?.id
                  ) {
                    try {
                      const sessionOrders = await getDocs(query(
                        collection(db, 'orders'),
                        where('customerId', '==', selectedSession.id)
                      ));
                      const totalAmount = sessionOrders.docs.reduce(
                        (sum, d) => sum + (parseFloat(d.data().totalPrice) || 0), 0
                      );
                      await recalculateCommissionForUser(
                        db,
                        salesUserIdToSave,
                        selectedSession.id,
                        selectedSession.name || '',
                        totalAmount,
                        true,
                        selectedSession.orderDate || selectedSession.sessionDate
                      );
                    } catch (commErr) {
                      console.error('Prim uygulanırken hata:', commErr);
                    }
                  }
                  // salesUserId'yi session dokümanına kaydet (silme/iade için gerekli)
                  try {
                    await updateDoc(doc(db, 'dealer_sessions', selectedSession.id), {
                      salesUserId: salesUserIdToSave,
                      updatedAt: new Date()
                    });
                    setSessions(prev => prev.map(s =>
                      s.id === selectedSession.id ? { ...s, salesUserId: salesUserIdToSave } : s
                    ));
                  } catch (saveErr) {
                    console.error('Session salesUserId kaydedilemedi:', saveErr);
                  }
                  if (editingDealer) {
                    fetchDealerSessions(editingDealer.id);
                  }
                  setSelectedSession(null);
                  setIsOrderFormOpen(false);
                  setSelectedSalesUserId(null);
                  showToast(t('dealers.session.completeSuccess'));
                }}>
                <i className="fas fa-check-circle"></i> {t('dealers.session.completeAction')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DealerList;
